import type { ComponentType } from 'react';
import type { ViewId } from '@/shell/navigation';
import { CORE_PAGES, CORE_CHILD_ROUTES } from './core/routes';
import { InventoryPage, INVENTORY_CHILD_ROUTES } from './inventory/routes';
import { BILLING_CHILD_ROUTES, InvoicesPage } from './billing/routes';
import { RoutingPage } from './routing/routes';
import { PayrollPage } from './payroll/routes';

/** Composition root: optional screens are registered here, never imported by core routes. */
export const IMPLEMENTED: Record<string, ComponentType> = {
  ...CORE_PAGES,
  inventory: InventoryPage,
  invoices: InvoicesPage,
  map: RoutingPage,
  payroll: PayrollPage,
};
export const CHILD_ROUTES: typeof CORE_CHILD_ROUTES = {
  ...CORE_CHILD_ROUTES,
  ...INVENTORY_CHILD_ROUTES,
  ...BILLING_CHILD_ROUTES,
};

/**
 * The key a view's screen is registered under in the two tables above.
 *
 * The keys are the route ids the feature owners already extend, so they stay as they are and this
 * table says which view each belongs to. A view absent here has no route of its own: Today is the
 * home route, Field has its own chrome, and Pricing approvals is a child route of Proposals.
 */
const PAGE_KEYS: Readonly<Partial<Record<ViewId, string>>> = {
  board: 'dispatch',
  map: 'map',
  jobs: 'jobs',
  customers: 'customers',
  deals: 'pipeline',
  leads: 'leads',
  proposals: 'proposals',
  handoffs: 'replacement',
  installations: 'handoff',
  followups: 'followup',
  invoices: 'invoices',
  payroll: 'payroll',
  catalogue: 'pricebook',
  stock: 'inventory',
  reports: 'reports',
  team: 'team',
  settings: 'settings',
  'my-handoffs': 'replacement-handoff',
};

/**
 * Where a view's screen and child routes are registered.
 *
 * @param view the view
 * @returns the key into {@link IMPLEMENTED} and {@link CHILD_ROUTES}, or undefined when the view
 *     has no route of its own
 */
export function pageKeyOf(view: ViewId): string | undefined {
  return PAGE_KEYS[view];
}
